import React, { useEffect, useState } from "react";
import { object, func, array } from "prop-types";
import { LazyLoadImages, SocialShare } from "@quintype/components";
import { SocialShareTemplate } from "../../../arrow/components/Molecules/SocialShareTemplate";
import { StoryHeader, LiveBlogHighlights } from "../../molecules";
import AdComponent from "../../molecules/AdComponent";
import { PublishDateAndTime } from "../../atoms";
import { StoryElementCard } from "../../../arrow/components/Molecules/StoryElementCard";
import { LiveIcon } from "../../../arrow/components/Svgs/liveicon";
import { StoryTags } from "../../../arrow/components/Atoms/StoryTags";
import KeyEventCards from "../../../arrow/components/Molecules/KeyEvents";
import { EntityListingStory } from "../../atoms/EntityListingStory";
import { useDeviceType } from "../../customHooks/useDeviceType";

import "./live-blog.m.css";

const LiveBlogStory = ({ story, config, adWidget, adPlaceholder, hasAccess, recommendedStories, params = {} }) => {
  const [newStory, setStory] = useState(story);
  const [showHighlights, setShowHighlights] = useState(false);
  const [showCards, setShowCards] = useState(20);
  const [initialCount, setInitialCount] = useState(0);
  const { headline, authors } = newStory;
  const cardsLength = newStory?.cards.length;
  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";

  useEffect(() => {
    const intervalId = setInterval(getUpdatedStory, 15000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (showHighlights && isMobile) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }, [showHighlights]);

  const scrollToCard = (cardId) => {
    if (cardId) {
      setTimeout(() => {
        const element = document?.getElementById(cardId);
        if (element) {
          const offset = 72;
          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: elementPosition - offset,
            behavior: "smooth",
          });
        }
      }, 10);
    }
  };

  useEffect(() => {
    scrollToCard(params.cardId);
  }, []);

  const getUpdatedStory = async () => {
    const stories = await fetch(`/api/v1/stories/${story.id}`).then((res) => res.json());
    setStory(stories.story);
  };

  const handleReadMore = () => {
    setShowCards(() => showCards + 10);
    setInitialCount(() => initialCount + 10);
  };

  return (
    <div>
      <LiveBlogHighlights
        story={newStory}
        isOpen={showHighlights}
        setShowHighlights={setShowHighlights}
        scrollToCard={scrollToCard}
      />
      <div id="live-blog-container" styleName="story-template-container" className="story-holder">
        <StoryHeader
          story={newStory}
          headline={headline}
          storyAuthorData={authors}
          publishTime={newStory?.["published-at"]}
          readTime={newStory?.["read-time"]}
          setShowHighlights={setShowHighlights}
        />

        <EntityListingStory story={story} config={config} />
        <div id="story-wrapper" styleName="story-body">
          <KeyEventCards
            story={newStory}
            scrollToCard={scrollToCard}
            config={{
              buttonText: "See More",
            }}
          />
          <div styleName="live-updates">Live Updates</div>
          <div styleName="live-blog-cards">
            {newStory?.cards.slice(initialCount, showCards).map((card, index) => {
              return (
                <>
                  <div
                    styleName={`live-blog-card ${index === 0 || index % 2 !== 0 ? "border-extra" : ""}`}
                    key={index}
                    id={card.id}
                  >
                    <div styleName="timestamp-and-share">
                      <div styleName="timestamp">
                        <LiveIcon color="#C6554F" />
                        <PublishDateAndTime publishTime={card["card-added-at"]} />
                      </div>
                      <div styleName="share-svg">
                        <SocialShare
                          template={SocialShareTemplate}
                          fullUrl={encodeURI(`${newStory.url}#${card.id}`)}
                          title={story.headline}
                          shareIconId={`share-icon-story-share-${story.id}`}
                          story={story}
                        />
                      </div>
                    </div>

                    <div styleName="event-content" id={`card${card.id}`}>
                      <LazyLoadImages>
                        <StoryElementCard card={card} story={newStory} key={index} />
                      </LazyLoadImages>
                    </div>
                  </div>
                  {index % 2 === 0 && (
                    <AdComponent
                      recommendedStories={recommendedStories}
                      isDesktop={!isMobile}
                      story={newStory}
                      key={index}
                      adIndexNo={index / 2}
                    />
                  )}
                </>
              );
            })}
          </div>
          {cardsLength >= showCards ? (
            <button onClick={() => handleReadMore()} styleName="load-more-button">
              Load More
            </button>
          ) : null}
          <StoryTags tags={story.tags} template="noBorder"  story={story} />
        </div>
      </div>
    </div>
  );
};

LiveBlogStory.propTypes = {
  story: object,
  config: object,
  adWidget: func,
  adPlaceholder: object,
  params: object,
  relatedStories: object,
  hasAccess: func,
  recommendedStories: array,
};

export default LiveBlogStory;
